import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import FullBlockingModal from '../common/FullBlockingModal';
import { Button } from '../common/Button';
import ProfileAvatar from '../common/ProfileAvatar';
import { useQuoteStore } from '../../stores/quoteStore';
import { QuoteAlliance } from '../../types/quote';

type BasicFreelancer = {
  id: string;
  first_name: string;
  avatar_url?: string | null;
};

interface QuoteAllianceInviteModalProps {
  freelancer: BasicFreelancer;
  quoteId: string;
  serviceCategoryId: number;
  onClose: () => void;
  invitedFreelancerId?: string;
  job_title?: string | null;
}

export default function QuoteAllianceInviteModal({
  freelancer,
  quoteId,
  serviceCategoryId,
  onClose,

  invitedFreelancerId,
  job_title,
}: QuoteAllianceInviteModalProps) {
  const { t } = useTranslation(['alliances', 'quotes', 'common']);
  const { inviteQuoteAlliance, retireQuoteAlliance } = useQuoteStore();

  const [step, setStep] = useState<'form' | 'sent'>('form');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quoteAllianceId, setQuoteAllianceId] = useState<string | null>(null);

  const targetId = invitedFreelancerId ?? freelancer.id;

  const handleSend = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = (await inviteQuoteAlliance(
        quoteId,
        targetId,
        serviceCategoryId,
        message.trim()
      )) as unknown as QuoteAlliance;
      const id = res?.id ?? null;
      setQuoteAllianceId(id);
      setStep('sent');
    } catch (e) {
      console.error(e);
      setError(
        t('createQuoteModal.unknownInviteAllianceError', {
          defaultValue: 'Une erreur est survenue lors de l’envoi.',
        })
      );
    } finally {
      setBusy(false);
    }
  };

  const handleCancelInvite = async () => {
    if (!quoteAllianceId) return;
    setBusy(true);
    setError(null);
    try {
      await retireQuoteAlliance(quoteAllianceId);
    } catch (e) {
      console.error(e);
      setError(
        t('createQuoteModal.unknownCancelAllianceRequestError', {
          defaultValue: 'Impossible d’annuler la demande.',
        })
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[64rem]">
      <button
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl md:top-0"
        onClick={onClose}
        aria-label={t('common:form_actions.close')}
      >
        &times;
      </button>

      {step === 'form' ? (
        <>
          <h2 className="font-bold text-2xl text-secondary">
            <Trans
              i18nKey={'quotes:createQuoteModal.inviteAllianceQuoteTitle'}
              ns={'quotes'}
              values={{ jobTitle: job_title || '' }}
              components={{ bold: <span className="font-bold" /> }}
            />
          </h2>

          <div className="flex items-center gap-3 mt-4">
            <ProfileAvatar
              profile_id={freelancer.id}
              first_name={freelancer.first_name}
              avatar_url={freelancer.avatar_url}
              className="flex-shrink-0"
            />
            <div className="text-secondary">
              <span className="font-bold">{freelancer.first_name}</span>
            </div>
          </div>

          <div className="flex flex-col gap-2 mt-6 mb-2">
            <label className="text-secondary" htmlFor="inviteMessage">
              <Trans
                i18nKey={'allianceMessageForLabel'}
                ns={'alliances'}
                values={{ freelancerName: freelancer.first_name }}
                components={{ bold: <span className="font-bold" /> }}
              />
            </label>

            <textarea
              id="inviteMessage"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder={t('quotes:createQuoteModal.inviteMessagePlaceholder', {
                defaultValue: 'Explique brièvement le rôle et le contexte (max 1000 caractères)…',
              })}
              maxLength={1000}
              className="w-full h-40 border-0 px-3 py-2 mt-2 font-ambit text-sm leading-5 bg-[#E1E9F0]"
            />
            {error && <div className="text-[#F04438] text-sm font-ambit mt-1">{error}</div>}
          </div>

          <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center mt-6">
            <Button className="w-full md:w-auto md:px-8" onClick={handleSend} disabled={busy}>
              {busy
                ? t('quotes:createQuoteModal.sendingQuoteAllianceRequest', {
                    defaultValue: 'Envoi de la demande…',
                  })
                : t('quotes:createQuoteModal.sendQuoteAllianceRequest', {
                    defaultValue: 'Envoyer la demande',
                  })}
            </Button>
            <button
              className="block font-bold mx-auto text-secondary underline leading-tight md:mx-16"
              onClick={onClose}
            >
              {t('quotes:close', { defaultValue: 'Fermer' })}
            </button>
          </div>
        </>
      ) : (
        <>
          <h2 className="text-2xl font-bold text-secondary">
            {t('quotes:createQuoteModal.inviteSentTitle', { defaultValue: 'Demande envoyée' })}
          </h2>
          <p className="text-secondary text-[0.9375rem] font-ambit mt-2 mb-8">
            <Trans
              i18nKey={'createQuoteModal.inviteSentMessage'}
              ns={'quotes'}
              values={{ freelancerName: freelancer.first_name }}
              components={{ bold: <span className="font-bold" /> }}
            />
          </p>
          {error && <div className="text-[#F04438] text-sm font-ambit mt-1">{error}</div>}
          <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
            <Button className="w-full md:w-auto md:px-8" onClick={onClose}>
              {t('quotes:createQuoteModal.backToQuote', { defaultValue: 'Retour au devis' })}
            </Button>
            {quoteAllianceId && (
              <button
                className="block font-bold mx-auto text-secondary underline leading-tight md:mx-16"
                onClick={handleCancelInvite}
                disabled={busy}
              >
                {busy
                  ? t('quotes:createQuoteModal.cancellingQuoteAllianceRequest', {
                      defaultValue: 'Annulation…',
                    })
                  : t('quotes:createQuoteModal.cancelQuoteAllianceRequest', {
                      defaultValue: 'Annuler la demande',
                    })}
              </button>
            )}
          </div>
        </>
      )}
    </FullBlockingModal>
  );
}
